/** Purchase requisition / purchase order types. */

export type PurchaseCostObjectType = 'overhead' | 'project';

/**
 * Required spend classification on create/edit.
 * Cost-object type: job -> project; overhead | capex -> overhead.
 * Approval routing uses spend class directly (CapEx does not follow the
 * overhead supervisor / COO ladder).
 */
export type PurchaseSpendClass = 'job' | 'overhead' | 'capex';

/** Editable lookup for non-job discretionary OpEx buckets. */
export type PurchaseOverheadGroup =
  | 'shop_tools_small_equipment'
  | 'facility_office_improvements'
  | 'safety_consumables'
  | 'software_admin'
  | 'training_crew_enablement'
  | 'crew_investment'
  | 'marketing'
  | 'vehicles';

/** Job PO node — maps onto Project P&L buckets (`timber` is other materials, not the timber package). */
export type PurchaseJobPlBucket =
  | 'design'
  | 'engineering'
  | 'timber'
  | 'production'
  | 'freight'
  | 'install'
  | 'travel';

/** QuickBooks vendor-bill link for an issued Bill Payment PO. */
export type PurchaseQbBillStatus = 'linked' | 'needs_vendor' | 'needs_bill' | 'pending' | 'skipped';

export type PurchaseRequisitionStatus =
  | 'draft'
  | 'pending_approval'
  | 'in_review'
  | 'approved'
  | 'purchased'
  | 'returned'
  | 'rejected'
  | 'cancelled';

/** How the requisition was created. Crew Slack recommendations use employee_recommendation. */
export type PurchaseOrigin =
  | 'employee_recommendation'
  | 'other_quotes'
  | 'designer_invoice'
  | 'manual';

export type ApprovalGateId =
  | 'supervisor'
  | 'project_manager'
  | 'department_head'
  | 'controller'
  | 'executive';

export type ApprovalStepStatus = 'pending' | 'approved' | 'skipped' | 'returned' | 'rejected';

export interface PurchaseCostObject {
  type: PurchaseCostObjectType;
  /**
   * Spend class for create/edit. Legacy rows without this field are inferred
   * from `type` (project -> job, overhead -> overhead).
   */
  spendClass?: PurchaseSpendClass;
  /** Required when spendClass is overhead. */
  overheadGroup?: PurchaseOverheadGroup;
  /**
   * When set, this overhead PO pays that forecast line (overheadExpenses id)
   * and is not drawn from the OpEx group budget. Blank means group budget only.
   */
  overheadExpenseId?: string;
  /** Required when spendClass is job. */
  plBucket?: PurchaseJobPlBucket;
  /** Enhanced deal id when tagged to a job. */
  dealId?: string;
  /** Timber project id when one can be resolved. */
  projectId?: string;
  label?: string;
}

export type PurchaseAttachmentKind = 'quote' | 'receipt';

export interface PurchaseAttachment {
  name: string;
  storagePath: string;
  contentType?: string;
  size?: number;
  url?: string;
  /** Quote / docs on submit; receipt after purchase. Legacy files have no kind. */
  kind?: PurchaseAttachmentKind;
  /** SHA-256 hex of file bytes — agent attach dedupe key. */
  contentHash?: string;
  attachedById?: string;
  attachedByName?: string;
  /** Agent key / persona that attached (e.g. hugh, caesar). */
  attachedViaAgentId?: string;
  /** ISO timestamp when an agent attached the file. */
  attachedAt?: string;
}

export interface PurchaseLineItem {
  id: string;
  description: string;
  quantity: number;
  /** Listed / catalog unit price before tax. */
  unitPrice: number;
  /** When false, listed amount is used as-is (legacy lines and tax-exempt). */
  taxable: boolean;
  /** Per-line sales-tax percent from the purchase API. Omit to use the PR rate. */
  taxRate?: number;
  /** Dollar tax for this line. Used with taxRate; an explicit amount wins over the rate. */
  taxAmount?: number;
  /** Designer-invoice hours. Informational; cash is still the line amount. */
  hours?: number;
  /** Product page Hugh attached on this line. */
  productUrl?: string;
  productLabel?: string;
  /** Store for this line. Blank lines use the requisition vendor. */
  vendorName?: string;
  /** Purchase order this line joined when the requisition was issued. */
  poNumber?: string;
}

export interface PurchaseLink {
  id: string;
  url: string;
  label?: string;
}

export interface PurchasingHoldReason {
  id: string;
  label: string;
  /** Suggested visibility when a reviewer picks this reason. */
  visibleToRequesterDefault: boolean;
}

/** Fields the requester is allowed to see. Internal reason/note live separately. */
export interface PurchaseHoldPublic {
  visibleToRequester: boolean;
  publicLabel?: string;
  resumeStatus: PurchaseRequisitionStatus;
}

export interface PurchaseVarianceRequest {
  proposedAmount: number;
  requestedById: string;
  requestedByName: string;
  requestedAt: Date;
  reviewerId: string;
  reviewerName: string;
  /** Set when the receipt review is for one supplier PO on a multi-PO requisition. */
  poNumber?: string;
}

/** One supplier PO marked purchased while the requisition is still open. */
export interface PurchasedSupplierOrder {
  poNumber: string;
  vendorName?: string;
  purchasedAt: Date;
  purchasedById: string;
  purchasedByName: string;
  purchasedAmount: number;
}

/** Who changed amount / purchasedAmount after purchase (required note). */
export interface PurchaseAmountEditAuditEntry {
  at: Date;
  byId: string;
  byName: string;
  note: string;
  previousAmount: number;
  nextAmount: number;
  previousPurchasedAmount: number | null;
  nextPurchasedAmount: number | null;
  kind: 'within_tolerance' | 'unchanged_fields' | 'authority_confirm';
}

export interface PurchaseHoldInternal {
  reasonId: string;
  reasonLabel: string;
  note?: string;
  visibleToRequester: boolean;
  heldById: string;
  heldByName: string;
  heldAt: Date;
}

export interface PurchaseApprovalStep {
  gate: ApprovalGateId;
  roleLabel: string;
  userId: string;
  userName: string;
  status: ApprovalStepStatus;
  decidedAt?: Date;
  note?: string;
}

export interface PurchaseRequisition {
  id: string;
  prNumber: string;
  poNumber?: string;
  requesterId: string;
  requesterName: string;
  title: string;
  justification: string;
  vendorName?: string;
  amount: number;
  /**
   * Amount that last cleared approval gates. Post-issue edits of 10% or less
   * stay issued; larger moves are measured against this baseline (not stacked).
   */
  approvedAmount?: number;
  /** Sales-tax percent locked in at submit. Missing on legacy PRs. */
  salesTaxPct?: number;
  lineItems: PurchaseLineItem[];
  links: PurchaseLink[];
  costObject: PurchaseCostObject;
  /** Denormalized from costObject for queries (Project P&L, OpEx remaining). */
  spendClass?: PurchaseSpendClass;
  dealId?: string;
  plBucket?: PurchaseJobPlBucket;
  overheadGroup?: PurchaseOverheadGroup;
  /** Denormalized from costObject. Set when the PO pays a known forecast line. */
  overheadExpenseId?: string;
  attachments: PurchaseAttachment[];
  status: PurchaseRequisitionStatus;
  approvalSteps: PurchaseApprovalStep[];
  currentStepIndex: number;
  /** Denormalized for Firestore array-contains queries. */
  approvalStepUserIds: string[];
  currentApproverId?: string;
  bandLabel?: string;
  paymentMethod?: string;
  /** Stable pay-method id (`company_card`, `ach_office`, `project_card`, …). */
  paymentMethodId?: string;
  paymentMethodNote?: string;
  /** Other Quotes upload that created this job PO. */
  rfqSubmissionId?: string;
  rfqProjectId?: string;
  /** Designer invoice that issued this job PO (Approve and Issue PO). */
  designerInvoiceId?: string;
  designerInvoiceNumber?: string;
  /**
   * When false, skip Billing & Payables supplierBills on issue
   * (card methods, or estimating/precon designer invoices).
   * Undefined/true keeps historical ACH payable behavior.
   */
  createSupplierBill?: boolean;
  /** Intake origin — crew Slack wish-list / damaged-tool recommendations. */
  origin?: PurchaseOrigin;
  /** Crew Slack report kind when origin is employee_recommendation. */
  crewReportKind?: 'damaged_tool' | 'wish_list' | 'purchasable';
  source?: string;
  /** An agent created this requisition on behalf of the requester. */
  createdViaAgent?: boolean;
  /** Agent id that filed the requisition, such as `hugh`. */
  createdByAgentId?: string;
  /** Agent issued the PR and PO together because the buy already happened. COO still reviews. */
  alreadyPurchased?: boolean;
  /** Short chip from the bot. At most two words. Replaces Employee recommendation when set. */
  badge?: string;
  /** QuickBooks vendor bill created (or attempted) for this Bill Payment PO. */
  qbBillStatus?: PurchaseQbBillStatus;
  qbBillId?: string;
  qbBillUrl?: string;
  qbBillMessage?: string;
  hold?: PurchaseHoldPublic;
  purchasedAt?: Date;
  purchasedById?: string;
  purchasedByName?: string;
  /** Receipt / card total confirmed at purchase — used to match QuickBooks. */
  purchasedAmount?: number;
  /**
   * Supplier POs already marked purchased on a multi-PO requisition.
   * The document stays `approved` until every issued PO number is in this list.
   */
  orderPurchases?: PurchasedSupplierOrder[];
  /** `supplier_bill` when Billing & Payables closed this PO after the final payment. */
  purchasedVia?: 'supplier_bill';
  /**
   * Audit trail when staff/bots edit amount or purchasedAmount after purchase.
   * Does not overwrite purchasedAt / purchasedBy*.
   */
  amountEditHistory?: PurchaseAmountEditAuditEntry[];
  /** Latest Sequence / QB card expense linked to this card PO. */
  qbExpenseId?: string;
  /** All card expenses mapped to this PO (partial charges, tax, freight). */
  qbExpenseIds?: string[];
  sequenceTransactionId?: string;
  sequenceTransactionIds?: string[];
  /** Pending actual-amount review when the receipt is more than 3% off the PO. */
  purchaseVariance?: PurchaseVarianceRequest;
  createdAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
  cancelledAt?: Date;
  cancelledById?: string;
  cancelledByName?: string;
}

export interface PurchasingPaymentMethod {
  id: string;
  label: string;
}

export interface PurchasingAuthorityBand {
  id: string;
  minAmount: number;
  /** Exclusive. Omit / null on the last band. */
  maxAmount: number | null;
  gates: ApprovalGateId[];
  /** When set, used instead of `gates` for overhead requisitions. */
  overheadGates?: ApprovalGateId[];
  /** When set, used instead of `gates` for CapEx. Defaults to controller (CFO). */
  capexGates?: ApprovalGateId[];
}

export interface PurchasingPolicy {
  bands: PurchasingAuthorityBand[];
  paymentMethods: PurchasingPaymentMethod[];
  fallbackApproverUserId?: string;
  /** Combined sales tax applied to listed line totals. */
  salesTaxPct: number;
  holdReasons: PurchasingHoldReason[];
  updatedAt?: Date;
  updatedBy?: string;
}

/** Org chart layer. Legacy `office` / `shop` values normalize to `general` on read. */
export type OrgLevel = 'executive' | 'managerial' | 'general';
/** Org chart group. Legacy `back_office` -> `office`, `operations` -> `shop` on read. */
export type OrgDepartment = 'sales' | 'office' | 'shop';

export const ORG_LEVELS: OrgLevel[] = ['executive', 'managerial', 'general'];
export const ORG_DEPARTMENTS: OrgDepartment[] = ['sales', 'office', 'shop'];

/**
 * Seat derived from title / designations / 1099 flag. Every seat except `staff`
 * is pinned: its layer, group, and manager come from the reporting rules, not
 * from drag-and-drop.
 */
export type OrgSeat = 'ceo' | 'coo' | 'cfo' | 'office_manager' | 'contractor' | 'staff';

export interface OrgChartPerson {
  uid: string;
  displayName: string;
  title?: string;
  photoURL?: string | null;
  role: string;
  isActive: boolean;
  /** Stored manager (`users/{uid}.reportsToUserId`). */
  reportsToUserId?: string;
  designations?: string[];
  orgLevel?: OrgLevel;
  orgDepartment?: OrgDepartment;
  is1099Contractor?: boolean;
  /** CRM test / bot login. Charted in the Bots lane below Staff. */
  isTestUser?: boolean;
  executiveBriefingProfile?: 'ceo' | 'coo' | null;
  orgSeat?: OrgSeat;
  pinned?: boolean;
  /** Manager after the reporting rules run — what the chart draws and what Purchasing should read. */
  effectiveReportsToUserId?: string;
}

export interface OrgChartNode {
  person: OrgChartPerson;
  children: OrgChartNode[];
}
